import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import api, { formatVND } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown, ChevronUp, Calendar, Clock, MapPin, Loader2, History, Zap,
  CreditCard, BadgeCheck, CircleDollarSign, Image as ImageIcon, Upload, XCircle,
  Target, Car, Volleyball, Plus, Trash2,
} from "lucide-react";
import PhoneVerifyDialog from "@/components/PhoneVerifyDialog";
import PaymentReceiptDialog from "@/components/PaymentReceiptDialog";

const START_HOUR = 5;
const END_HOUR = 22;
const SLOT_MINUTES = 30;
const SLOT_WIDTH = 64;
const ROW_HEIGHT = 52;
const SIDEBAR_WIDTH = 110;

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function formatDateVN(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const days = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
  return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function timeToMin(t) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function minToTime(m) { return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; }

function currentMinOfDay() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

const TIME_OPTIONS = [];
for (let m = START_HOUR * 60; m < END_HOUR * 60; m += 30) {
  TIME_OPTIONS.push(minToTime(m));
}
TIME_OPTIONS.push(minToTime(END_HOUR * 60));

const ADDON_LIST = [
  { key: "addon_ball_picker", label: "Yêu cầu nhân viên nhặt bóng", icon: Volleyball, activeCls: "bg-teal-500/10 border-teal-500/30 text-teal-400", checkCls: "border-teal-500 bg-teal-500 text-white" },
  { key: "addon_ball_machine", label: "Yêu cầu máy bắn bóng", icon: Target, activeCls: "bg-sky-500/10 border-sky-500/30 text-sky-400", checkCls: "border-sky-500 bg-sky-500 text-white" },
  { key: "addon_training_cart", label: "Yêu cầu xe tập bóng", icon: Car, activeCls: "bg-amber-500/10 border-amber-500/30 text-amber-400", checkCls: "border-amber-500 bg-amber-500 text-white" },
];

export default function CustomerBookingPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const today = todayStr();
  const maxDate = addDays(today, 2);
  // If past 22:00, default to tomorrow
  const initialDate = new Date().getHours() >= 22 ? addDays(today, 1) : today;
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [bookCourt, setBookCourt] = useState(null);
  const [bookStart, setBookStart] = useState("");
  const [bookEnd, setBookEnd] = useState("");
  const [bookNotes, setBookNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const bodyScrollRef = useRef(null);
  // Add-ons state
  const [addonBallPicker, setAddonBallPicker] = useState(false);
  const [addonBallMachine, setAddonBallMachine] = useState(false);
  const [addonTrainingCart, setAddonTrainingCart] = useState(false);
  const [addonRacketCount, setAddonRacketCount] = useState(0);
  const [guestCount, setGuestCount] = useState(1);
  // Multi-court state: danh sách các sân thêm (ngoài bookCourt chính).
  // Mỗi item: {court_id, start_time, end_time}
  const [extraSlots, setExtraSlots] = useState([]);
  // Multi-time state: danh sách KHUNG GIỜ THÊM cho CÙNG sân chính (bookCourt).
  // Mỗi item: {start_time, end_time} — court_id ngầm = bookCourt.id.
  const [sameCourtSlots, setSameCourtSlots] = useState([]);
  // Mobile collapse state
  const [expandedCourts, setExpandedCourts] = useState({});
  // Receipt dialog + viewer state
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptBookingId, setReceiptBookingId] = useState(null);
  const [viewReceiptUrl, setViewReceiptUrl] = useState(null);

  const handleConfirmPayment = (bookingId) => {
    setReceiptBookingId(bookingId);
    setReceiptDialogOpen(true);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["customer-bookings", selectedDate],
    queryFn: () => api.get(`/customer/bookings?date=${selectedDate}`).then(r => r.data),
  });
  const { data: historyData } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => api.get("/customer/my-bookings").then(r => r.data),
    enabled: showHistory && !!user,
  });

  // Show only regular courts (Sân 1-8) on timeline, Social via separate button
  const courts = data?.courts || [];
  const regularCourts = courts.filter(c => c.court_type !== "social").sort((a, b) => {
    // Sort by court number extracted from name (Sân 1, Sân 2, ..., Sân 8)
    const numA = parseInt(a.name?.replace(/\D/g, "") || "0");
    const numB = parseInt(b.name?.replace(/\D/g, "") || "0");
    return numA - numB;
  });
  const socialCourt = courts.find(c => c.court_type === "social");
  const allCourts = regularCourts;
  // Filter social bookings from timeline display
  const bookings = (data?.bookings || []).filter(b => b.booking_type !== "social");

  // Social booking modal state
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [socialSession, setSocialSession] = useState("morning"); // "morning" | "evening"
  const [socialGuests, setSocialGuests] = useState(1);
  const [socialNotes, setSocialNotes] = useState("");
  const [socialSubmitting, setSocialSubmitting] = useState(false);

  const isToday = selectedDate === today;
  const nowMin = currentMinOfDay();
  const startMin = isToday ? Math.max(START_HOUR * 60, Math.floor(nowMin / SLOT_MINUTES) * SLOT_MINUTES) : START_HOUR * 60;
  const endMin = END_HOUR * 60;
  const slots = useMemo(() => {
    const arr = [];
    for (let m = startMin; m < endMin; m += SLOT_MINUTES) arr.push(m);
    return arr;
  }, [startMin, endMin]);

  const canPrev = selectedDate > today;
  const canNext = selectedDate < maxDate;
  const goDate = (d) => { if (d >= today && d <= maxDate) setSelectedDate(d); };

  const toggleCourtExpanded = (courtId) => {
    setExpandedCourts(prev => {
      // Accordion: if clicking same court, toggle off; otherwise open only this one
      if (prev[courtId]) return {};
      return { [courtId]: true };
    });
  };

  const handleClickSlot = (court, slotMin) => {
    if (!user?.phone) { setPhoneVerifyOpen(true); return; }
    const start = minToTime(slotMin);
    const isSocial = court.court_type === "social";
    const courtBookings = bookings.filter(b => b.court_id === court.id);

    // Find the nearest booking AFTER slotMin to cap the end time
    let maxEndM = Math.min(slotMin + 120, endMin); // default 2h or end of day
    if (!isSocial) {
      courtBookings.forEach(b => {
        const bS = timeToMin(b.start_time);
        // If this booking starts after our slot, cap end time at its start
        if (bS > slotMin && bS < maxEndM) {
          maxEndM = bS;
        }
      });
    }

    // Check if the slot itself is inside an existing booking
    const slotBlocked = !isSocial && courtBookings.some(b => {
      if (b.customer_id === user?.id && isSocial) return false;
      const bS = timeToMin(b.start_time); const bE = timeToMin(b.end_time);
      return slotMin >= bS && slotMin < bE;
    });
    if (slotBlocked) { toast.error("Khung giờ này đã được đặt"); return; }

    // Ensure at least 30 minutes available
    if (maxEndM - slotMin < 30) { toast.error("Không đủ thời gian trống (tối thiểu 30 phút)"); return; }

    const end = minToTime(maxEndM);
    setBookCourt(court);
    setBookStart(start);
    setBookEnd(end);
    setBookNotes("");
    setAddonBallPicker(false);
    setAddonBallMachine(false);
    setAddonTrainingCart(false);
    setAddonRacketCount(0);
    setGuestCount(1);
    setExtraSlots([]);
    setSameCourtSlots([]);
    setBookModalOpen(true);
  };

  // ---- Multi-court helpers ----
  const usedCourtIds = useMemo(() => {
    const ids = new Set();
    if (bookCourt?.id) ids.add(bookCourt.id);
    extraSlots.forEach(s => { if (s.court_id) ids.add(s.court_id); });
    return ids;
  }, [bookCourt?.id, extraSlots]);

  const availableCourtsForExtra = useMemo(
    () => regularCourts.filter(c => !usedCourtIds.has(c.id)),
    [regularCourts, usedCourtIds],
  );

  const addExtraSlot = () => {
    if (availableCourtsForExtra.length === 0) {
      toast.error("Đã chọn hết các sân khả dụng");
      return;
    }
    // Default: cùng giờ với sân chính, sân = sân khả dụng đầu tiên
    setExtraSlots(prev => [
      ...prev,
      { court_id: availableCourtsForExtra[0].id, start_time: bookStart, end_time: bookEnd },
    ]);
  };

  const updateExtraSlot = (idx, patch) => {
    setExtraSlots(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeExtraSlot = (idx) => {
    setExtraSlots(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSameCourtSlot = (idx, patch) => {
    setSameCourtSlots(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const removeSameCourtSlot = (idx) => {
    setSameCourtSlots(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmitBooking = async () => {
    if (!bookCourt || !bookStart || !bookEnd) return;
    // Validate sameCourtSlots
    for (let i = 0; i < sameCourtSlots.length; i++) {
      const s = sameCourtSlots[i];
      if (!s.start_time || !s.end_time) { toast.error(`Khung giờ thêm #${i + 1}: thiếu giờ`); return; }
      if (timeToMin(s.end_time) <= timeToMin(s.start_time)) {
        toast.error(`Khung giờ thêm #${i + 1}: giờ kết thúc phải sau giờ bắt đầu`);
        return;
      }
    }
    // Validate extra slots
    for (let i = 0; i < extraSlots.length; i++) {
      const s = extraSlots[i];
      if (!s.court_id) { toast.error(`Sân thêm #${i + 1}: chưa chọn sân`); return; }
      if (!s.start_time || !s.end_time) { toast.error(`Sân thêm #${i + 1}: thiếu giờ`); return; }
      if (timeToMin(s.end_time) <= timeToMin(s.start_time)) {
        toast.error(`Sân thêm #${i + 1}: giờ kết thúc phải sau giờ bắt đầu`);
        return;
      }
    }

    const totalSlots = 1 + sameCourtSlots.length + extraSlots.length;
    setSubmitting(true);
    try {
      if (totalSlots === 1) {
        // Single-court single-slot flow giữ nguyên
        const { data: result } = await api.post("/customer/bookings", {
          court_id: bookCourt.id, date: selectedDate, start_time: bookStart, end_time: bookEnd, notes: bookNotes,
          guest_count: guestCount,
          addon_ball_picker: addonBallPicker,
          addon_ball_machine: addonBallMachine,
          addon_training_cart: addonTrainingCart,
          addon_racket_count: addonRacketCount,
        });
        toast.success(result.message || "Đặt sân thành công!");
      } else {
        // Multi-slot: gộp main + sameCourtSlots (cùng court) + extraSlots (khác court)
        const slots = [
          { court_id: bookCourt.id, start_time: bookStart, end_time: bookEnd },
          ...sameCourtSlots.map(s => ({ court_id: bookCourt.id, start_time: s.start_time, end_time: s.end_time })),
          ...extraSlots.map(s => ({ court_id: s.court_id, start_time: s.start_time, end_time: s.end_time })),
        ];
        const { data: result } = await api.post("/customer/bookings/multi", {
          date: selectedDate, notes: bookNotes, guest_count: guestCount,
          slots,
          addon_ball_picker: addonBallPicker,
          addon_ball_machine: addonBallMachine,
          addon_training_cart: addonTrainingCart,
          addon_racket_count: addonRacketCount,
        });
        toast.success(result.message || `Đặt ${slots.length} khung sân thành công!`);
      }
      setBookModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["customer-bookings", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object" && Array.isArray(detail.conflicts)) {
        const lines = detail.conflicts.slice(0, 3).map(c => `• ${c.court_name} ${c.start_time}-${c.end_time}: ${c.reason}`).join("\n");
        toast.error(detail.message || "Có sân trùng lịch", { description: lines });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Lỗi đặt sân"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSocial = async () => {
    if (!socialCourt) return;
    setSocialSubmitting(true);
    const startTime = socialSession === "morning" ? "05:00" : "14:00";
    const endTime = socialSession === "morning" ? "14:00" : "22:00";
    try {
      const { data: result } = await api.post("/customer/bookings", {
        court_id: socialCourt.id, date: selectedDate, start_time: startTime, end_time: endTime,
        notes: socialNotes, guest_count: socialGuests,
      });
      toast.success(result.message || "Đặt Social thành công!");
      setSocialModalOpen(false);
      setSocialGuests(1);
      setSocialNotes("");
      queryClient.invalidateQueries({ queryKey: ["customer-bookings", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi đặt sân"); }
    finally { setSocialSubmitting(false); }
  };

  const endOptions = useMemo(() => {
    if (!bookStart) return [];
    const sMin = timeToMin(bookStart);
    return TIME_OPTIONS.filter(t => timeToMin(t) > sMin && timeToMin(t) <= endMin);
  }, [bookStart, endMin]);

  // ---- Conflict helpers cho dropdown ----
  // Trả các khoảng [startMin, endMin) đã bị các booking khác chiếm trên sân này.
  // Lọc cancelled để slot bị huỷ không bị block.
  const getBlockedRangesForCourt = useCallback((courtId) => {
    if (!courtId) return [];
    return bookings
      .filter(b =>
        b.court_id === courtId &&
        b.booking_type !== "social" &&
        b.status !== "cancelled" &&
        b.start_time && b.end_time,
      )
      .map(b => [timeToMin(b.start_time), timeToMin(b.end_time)]);
  }, [bookings]);

  /**
   * Kết hợp các khoảng đã chiếm trên `courtId` từ:
   * - booking khác trong DB (getBlockedRangesForCourt)
   * - main slot (bookCourt + bookStart/bookEnd) NẾU courtId === bookCourt.id và `excludeMain=false`
   * - các sameCourtSlots (khung giờ cùng sân chính) — TRỪ slot có index === excludeSameIdx
   * - các extraSlots (sân khác) cùng courtId — TRỪ slot có index === excludeExtraIdx
   * Dùng để dropdown của 1 row tự loại trừ giờ mà các row khác đang chiếm.
   */
  const getCombinedBlockedRanges = useCallback((
    courtId,
    { excludeMain = false, excludeSameIdx = null, excludeExtraIdx = null } = {},
  ) => {
    const ranges = [...getBlockedRangesForCourt(courtId)];
    if (!excludeMain && bookCourt?.id === courtId && bookStart && bookEnd) {
      ranges.push([timeToMin(bookStart), timeToMin(bookEnd)]);
    }
    if (bookCourt?.id === courtId) {
      sameCourtSlots.forEach((s, i) => {
        if (i === excludeSameIdx) return;
        if (s.start_time && s.end_time) {
          ranges.push([timeToMin(s.start_time), timeToMin(s.end_time)]);
        }
      });
    }
    extraSlots.forEach((s, i) => {
      if (i === excludeExtraIdx) return;
      if (s.court_id === courtId && s.start_time && s.end_time) {
        ranges.push([timeToMin(s.start_time), timeToMin(s.end_time)]);
      }
    });
    return ranges;
  }, [getBlockedRangesForCourt, bookCourt?.id, bookStart, bookEnd, sameCourtSlots, extraSlots]);

  // Một mốc giờ `t` (làm START) bị chặn nếu nó nằm GIỮA 1 booking khác (s <= t < e).
  // t trùng `e` (booking đã end) → OK để bắt đầu booking mới.
  const isStartBlocked = useCallback((courtId, t, opts = {}) => {
    const ranges = getCombinedBlockedRanges(courtId, opts);
    return ranges.some(([s, e]) => t >= s && t < e);
  }, [getCombinedBlockedRanges]);

  // Với START đã chọn `startT`, trả mốc END LỚN NHẤT cho phép (không cross qua booking nào).
  // Nếu KHÔNG có booking nào sau startT → trả endMin (cuối ngày).
  const getMaxAllowedEnd = useCallback((courtId, startT, opts = {}) => {
    const ranges = getCombinedBlockedRanges(courtId, opts);
    let limit = endMin;
    ranges.forEach(([bs]) => {
      if (bs > startT && bs < limit) limit = bs;
    });
    return limit;
  }, [getCombinedBlockedRanges, endMin]);

  // Khi bookStart hoặc bookCourt đổi → tự cap bookEnd nếu vượt giới hạn mới
  useEffect(() => {
    if (!bookCourt?.id || !bookStart) return;
    const startT = timeToMin(bookStart);
    const maxEnd = getMaxAllowedEnd(bookCourt.id, startT, { excludeMain: true });
    if (timeToMin(bookEnd) > maxEnd) {
      setBookEnd(minToTime(Math.max(maxEnd, startT + 30)));
    }
  }, [bookStart, bookCourt?.id, bookEnd, getMaxAllowedEnd]);

  // ---- Same-court suggestion (cần đặt SAU isStartBlocked/getMaxAllowedEnd vì refs deps)
  const suggestNextSameCourtSlot = useCallback(() => {
    if (!bookCourt?.id || !bookEnd) {
      return { start_time: bookStart || minToTime(startMin), end_time: bookEnd || minToTime(startMin + 60) };
    }
    const afterEnd = timeToMin(bookEnd);
    const blockedNow = isStartBlocked(bookCourt.id, afterEnd, { excludeMain: true });
    if (!blockedNow && afterEnd + 60 <= endMin) {
      const max = getMaxAllowedEnd(bookCourt.id, afterEnd, { excludeMain: true });
      return { start_time: bookEnd, end_time: minToTime(Math.min(afterEnd + 60, max)) };
    }
    const fallbackStart = Math.min(afterEnd + 30, endMin - 60);
    return { start_time: minToTime(fallbackStart), end_time: minToTime(Math.min(fallbackStart + 60, endMin)) };
  }, [bookCourt?.id, bookStart, bookEnd, isStartBlocked, getMaxAllowedEnd, endMin, startMin]);

  const addSameCourtSlot = () => {
    if (!bookCourt?.id) return;
    setSameCourtSlots(prev => [...prev, suggestNextSameCourtSlot()]);
  };

  const STATUS_COLORS = {
    booked: "bg-slate-700/60 border-slate-600/40",
    confirmed: "bg-emerald-500/15 border-emerald-500/30",
    pending: "bg-amber-500/12 border-amber-500/25",
    completed: "bg-sky-500/10 border-sky-500/25",
  };
  const MY_STATUS = {
    pending: { label: "Chờ XN", color: "text-amber-400" },
    confirmed: { label: "Đã XN", color: "text-emerald-400" },
    completed: { label: "Hoàn thành", color: "text-slate-400 dark:text-slate-600 dark:text-slate-400" },
    cancelled: { label: "Đã huỷ", color: "text-red-400" },
  };

  // ------- MOBILE: Collapsed card layout -------
  const renderMobileView = () => (
    <div className="space-y-2 md:hidden">
      {allCourts.map(court => {
        const isSocial = court.court_type === "social";
        const courtBookings = bookings.filter(b => b.court_id === court.id);
        // For social: only show OWN bookings
        const visibleBookings = isSocial
          ? courtBookings.filter(b => b.is_own !== false && b.customer_id === user?.id)
          : courtBookings;
        const isExpanded = expandedCourts[court.id] || false;

        return (
          <div key={court.id} className={`bg-white dark:bg-[#1e293b] border rounded-xl overflow-hidden ${isSocial ? "border-teal-500/30" : "border-slate-300 dark:border-[#334155]"}`}>
            {/* Court header - always visible, click to expand */}
            <button
              onClick={() => toggleCourtExpanded(court.id)}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-200 dark:hover:bg-[#334155]/20 transition-colors"
              data-testid={`mobile-court-toggle-${court.id}`}
            >
              <div className="flex items-center gap-2">
                <MapPin className={`w-3.5 h-3.5 ${isSocial ? "text-teal-400" : "text-slate-400 dark:text-slate-600 dark:text-slate-400"}`} />
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{court.name}</span>
                {isSocial && <span className="text-[9px] bg-teal-500/20 text-teal-400 border border-teal-500/30 rounded px-1.5 py-0">SOCIAL</span>}
                {visibleBookings.length > 0 && (
                  <span className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500">({visibleBookings.length} đặt)</span>
                )}
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-900 dark:text-slate-500 dark:text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-900 dark:text-slate-500 dark:text-slate-500" />}
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-3 pb-3 space-y-2">
                <div className="grid grid-cols-4 gap-1.5">
                  {slots.map(slotMin => {
                    const slotEnd = slotMin + SLOT_MINUTES;
                    const booked = courtBookings.find(b => {
                      const bS = timeToMin(b.start_time); const bE = timeToMin(b.end_time);
                      return slotMin < bE && slotEnd > bS;
                    });
                    const isOwn = booked && booked.is_own !== false && booked.customer_id === user?.id;
                    const label = minToTime(slotMin);

                    // Social: hide other people's bookings, show as available
                    if (isSocial && booked && !isOwn) {
                      return (
                        <button key={slotMin} onClick={() => handleClickSlot(court, slotMin)}
                          className="bg-slate-100 dark:bg-[#0f172a] border border-slate-300 dark:border-[#334155] hover:border-teal-500/50 hover:bg-teal-500/10 rounded-lg px-2 py-2 text-center transition-colors"
                          data-testid={`m-slot-${court.id}-${slotMin}`}>
                          <span className="text-[10px] text-slate-700 dark:text-slate-300 block">{label}</span>
                          <span className="text-[9px] text-teal-500">Trống</span>
                        </button>
                      );
                    }
                    if (booked && !isOwn) {
                      return (
                        <div key={slotMin} className="bg-slate-700/40 rounded-lg px-2 py-2 text-center">
                          <span className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500 block">{label}</span>
                          <span className="text-[9px] text-slate-400 dark:text-slate-600">Đã đặt</span>
                        </div>
                      );
                    }
                    if (isOwn) {
                      const st = MY_STATUS[booked.status] || MY_STATUS.pending;
                      const showPayBtn = booked.status === "confirmed" && !booked.payment_confirmed_at;
                      return (
                        <div key={slotMin}
                          onClick={() => { if (showPayBtn) handleConfirmPayment(booked.id); }}
                          className={`${showPayBtn ? "cursor-pointer" : ""} bg-lime-500/15 border-2 border-lime-500/40 rounded-lg px-2 py-2 text-center shadow-sm shadow-lime-500/10`}>
                          <span className="text-[10px] text-lime-300 block font-bold">{label}</span>
                          <span className={`text-[9px] ${st.color} font-bold`}>{st.label}</span>
                          {showPayBtn && <span className="text-[8px] text-sky-400 block mt-0.5">Chờ TT</span>}
                        </div>
                      );
                    }
                    return (
                      <button key={slotMin} onClick={() => handleClickSlot(court, slotMin)}
                        className="bg-slate-100 dark:bg-[#0f172a] border border-slate-300 dark:border-[#334155] hover:border-teal-500/50 hover:bg-teal-500/10 rounded-lg px-2 py-2 text-center transition-colors"
                        data-testid={`m-slot-${court.id}-${slotMin}`}>
                        <span className="text-[10px] text-slate-700 dark:text-slate-300 block">{label}</span>
                        <span className="text-[9px] text-teal-500">Trống</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ------- DESKTOP: Timeline ngang -------
  const renderDesktopTimeline = () => {
    const totalWidth = slots.length * SLOT_WIDTH;
    return (
      <div className="hidden md:block bg-white dark:bg-[#1e293b] border border-slate-300 dark:border-[#334155] rounded-xl" style={{ maxHeight: "calc(100vh - 240px)" }} data-testid="customer-timeline">
        <div ref={bodyScrollRef} className="overflow-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
          <div style={{ minWidth: totalWidth + SIDEBAR_WIDTH }}>
            {/* Header row — sticky top */}
            <div className="flex sticky top-0 z-40 timeline-header" style={{ height: 36 }}>
              <div className="sticky left-0 z-50 shrink-0 border-r border-b border-slate-300 dark:border-[#334155] px-3 flex items-center timeline-sidebar-cell" style={{ width: SIDEBAR_WIDTH }}>
                <span className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500 font-medium">Sân</span>
              </div>
              <div className="relative border-b border-slate-300 dark:border-[#334155] flex-1" style={{ minWidth: totalWidth }}>
                {slots.map((m, i) => {
                  const isHour = m % 60 === 0;
                  return (
                    <div key={m} className={`absolute top-0 bottom-0 flex items-end pb-1.5 ${isHour ? "timeline-gridline-hour" : "timeline-gridline-quarter"}`} style={{ left: i * SLOT_WIDTH, width: SLOT_WIDTH }}>
                      <span className={`pl-1.5 ${isHour ? "text-[11px] text-slate-800 dark:text-slate-200 font-bold" : "text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500"}`}>{minToTime(m)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Court rows */}
            {allCourts.map(court => {
              const isSocial = court.court_type === "social";
              const courtBookings = bookings.filter(b => b.court_id === court.id);
              // For social: only show own bookings on the timeline
              const visibleBookings = isSocial
                ? courtBookings.filter(b => b.is_own !== false && b.customer_id === user?.id)
                : courtBookings;

              return (
                <div key={court.id} className="flex" style={{ height: ROW_HEIGHT }}>
                  <div className={`sticky left-0 z-20 shrink-0 border-r border-b border-slate-300 dark:border-[#334155] px-3 flex items-center gap-1.5 timeline-sidebar-cell ${isSocial ? "bg-teal-500/5" : ""}`} style={{ width: SIDEBAR_WIDTH }}>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{court.name}</span>
                    {isSocial && <span className="text-[8px] bg-teal-500/20 text-teal-400 rounded px-1">S</span>}
                  </div>
                  <div className="relative border-b border-slate-300 dark:border-[#334155] flex-1" style={{ minWidth: totalWidth }}>
                    {/* Grid lines */}
                    {slots.map((m, i) => (
                      <div key={m} className={`absolute top-0 bottom-0 ${m % 60 === 0 ? "timeline-gridline-hour" : "timeline-gridline-quarter"}`} style={{ left: i * SLOT_WIDTH }} />
                    ))}
                    {/* Available (clickable) slots */}
                    {slots.map((slotMin, i) => {
                      const slotEnd = slotMin + SLOT_MINUTES;
                      const booked = courtBookings.some(b => {
                        const bS = timeToMin(b.start_time); const bE = timeToMin(b.end_time);
                        return slotMin < bE && slotEnd > bS;
                      });
                      // Social courts: always show as available (others' bookings hidden)
                      if (booked && !isSocial) return null;
                      if (isSocial) {
                        // Check if OWN booking exists here
                        const ownBooked = visibleBookings.some(b => {
                          const bS = timeToMin(b.start_time); const bE = timeToMin(b.end_time);
                          return slotMin < bE && slotEnd > bS;
                        });
                        if (ownBooked) return null;
                      }
                      return (
                        <div key={slotMin} onClick={() => handleClickSlot(court, slotMin)}
                          className={`absolute top-1 bottom-1 rounded-md cursor-pointer border transition-all ${isSocial ? "border-teal-500/15 hover:border-teal-500/50 hover:bg-teal-500/10 bg-teal-500/[0.05]" : "border-teal-500/10 hover:border-teal-500/50 hover:bg-teal-500/10 bg-teal-500/[0.03]"}`}
                          style={{ left: i * SLOT_WIDTH + 1, width: SLOT_WIDTH - 2 }}
                          data-testid={`slot-${court.id}-${slotMin}`} />
                      );
                    })}
                    {/* Booking bars - only show own for social */}
                    {visibleBookings.map(b => {
                      const bSMin = timeToMin(b.start_time);
                      const bEMin = timeToMin(b.end_time);
                      const startIdx = slots.findIndex(s => s >= bSMin);
                      const endIdx = slots.findIndex(s => s >= bEMin);
                      const sI = startIdx >= 0 ? startIdx : 0;
                      const eI = endIdx >= 0 ? endIdx : slots.length;
                      const leftPx = sI * SLOT_WIDTH;
                      const widthPx = Math.max((eI - sI) * SLOT_WIDTH, SLOT_WIDTH);
                      const isOwn = b.is_own !== false && b.customer_id === user?.id;
                      const clr = isOwn ? "bg-lime-500/15 border-lime-500/40 border-2" : STATUS_COLORS.booked;
                      return (
                        <div key={b.id} className={`absolute top-1 bottom-1 rounded-lg ${clr} border flex items-center px-2.5 overflow-hidden cursor-pointer`}
                          style={{ left: leftPx, width: widthPx }}
                          onClick={() => { if (isOwn && b.status === "confirmed" && !b.payment_confirmed_at) handleConfirmPayment(b.id); }}>
                          {isOwn ? (
                            <div className="truncate flex items-center gap-1">
                              <span className="text-[11px] font-bold text-lime-300">{b.start_time}-{b.end_time}</span>
                              <span className={`text-[9px] font-bold ${(MY_STATUS[b.status] || MY_STATUS.pending).color}`}>({(MY_STATUS[b.status] || MY_STATUS.pending).label})</span>
                              {b.status === "confirmed" && !b.payment_confirmed_at && (
                                <span className="text-[8px] font-bold text-sky-400 bg-sky-500/20 px-1 py-0.5 rounded">Chờ TT</span>
                              )}
                              {b.total_price > 0 && <span className="text-[9px] text-lime-400">{formatVND(b.total_price)}đ</span>}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500">Đã đặt</span>
                          )}
                        </div>
                      );
                    })}
                    {/* Non-own regular bookings */}
                    {!isSocial && courtBookings.filter(b => !(b.is_own !== false && b.customer_id === user?.id)).map(b => {
                      const bSMin = timeToMin(b.start_time);
                      const bEMin = timeToMin(b.end_time);
                      const startIdx = slots.findIndex(s => s >= bSMin);
                      const endIdx = slots.findIndex(s => s >= bEMin);
                      const sI = startIdx >= 0 ? startIdx : 0;
                      const eI = endIdx >= 0 ? endIdx : slots.length;
                      const leftPx = sI * SLOT_WIDTH;
                      const widthPx = Math.max((eI - sI) * SLOT_WIDTH, SLOT_WIDTH);
                      return (
                        <div key={b.id} className={`absolute top-1 bottom-1 rounded-lg ${STATUS_COLORS.booked} border flex items-center px-2.5 overflow-hidden`}
                          style={{ left: leftPx, width: widthPx }}>
                          <span className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500">Đã đặt</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="customer-booking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-teal-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900 dark:text-[#F8FAFC]">Đặt sân</h1>
            <p className="text-slate-900 dark:text-slate-500 dark:text-slate-500 text-[11px]">Chọn khung giờ trống để đặt sân Pickleball</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}
          className={`border-slate-300 dark:border-[#334155] gap-1.5 text-xs ${showHistory ? "text-teal-400 border-teal-500/30" : "text-slate-400 dark:text-slate-600 dark:text-slate-400"}`} data-testid="toggle-history">
          <History className="w-3.5 h-3.5" /> Lịch sử đặt sân
        </Button>
      </div>

      {/* Date tabs */}
      <div className="flex gap-2">
        {[0, 1, 2].map(offset => {
          const d = addDays(today, offset);
          const active = d === selectedDate;
          const label = offset === 0 ? "Hôm nay" : offset === 1 ? "Ngày mai" : formatDateVN(d);
          return (
            <button key={d} onClick={() => goDate(d)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active ? "bg-teal-500 text-[#0f172a] shadow-lg shadow-teal-500/20" : "bg-white dark:bg-[#1e293b] border border-slate-300 dark:border-[#334155] text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-200 hover:border-slate-400"}`}
              data-testid={`date-tab-${offset}`}>
              <div className="text-xs font-bold">{label}</div>
              <div className="text-[10px] opacity-70">{formatDateVN(d)}</div>
            </button>
          );
        })}
      </div>

      {/* Social booking button */}
      {socialCourt && (
        <div className="flex items-center justify-between bg-teal-500/5 border border-teal-500/20 rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-teal-400">Đặt vé Social</p>
              <p className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500">Sáng 50.000đ · Chiều tối 80.000đ</p>
            </div>
          </div>
          <Button onClick={() => { if (!user?.phone) { setPhoneVerifyOpen(true); return; } setSocialModalOpen(true); }}
            className="bg-teal-500 hover:bg-teal-400 text-[#0f172a] font-bold text-xs gap-1.5 h-8" data-testid="social-book-btn">
            <Zap className="w-3.5 h-3.5" /> Đặt Social
          </Button>
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-4 text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-700/60 border border-slate-600/40" /> Đã đặt</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-slate-300 dark:border-[#334155] hover:border-teal-500" /> Trống</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-lime-500/15 border-2 border-lime-500/40" /> Của bạn</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-teal-500/10 border border-teal-500/30" /> Social</span>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
      ) : (
        <>
          {renderDesktopTimeline()}
          {renderMobileView()}
        </>
      )}

      {/* Booking history */}
      {showHistory && (
        <div className="bg-white dark:bg-[#1e293b] border border-slate-300 dark:border-[#334155] rounded-xl p-4 space-y-3" data-testid="booking-history">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-[#F8FAFC] flex items-center gap-2"><History className="w-4 h-4 text-teal-400" /> Lịch sử đặt sân</h3>
          {!historyData?.bookings?.length ? (
            <p className="text-xs text-slate-900 dark:text-slate-500 dark:text-slate-500 py-4 text-center">Chưa có lịch sử đặt sân</p>
          ) : (
            <div className="space-y-2">
              {historyData.bookings.map(b => {
                const st = MY_STATUS[b.status] || MY_STATUS.pending;
                const createdAtStr = b.created_at ? (() => { const d = new Date(b.created_at); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")} ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; })() : "";
                return (
                  <div key={b.id} className="bg-slate-100 dark:bg-[#0f172a] border border-slate-300 dark:border-[#334155] rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <MapPin className="w-3 h-3 text-teal-400 shrink-0" />
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.court_name}</span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${st.color} bg-current/10`}>{st.label}</span>
                          {b.guest_count > 1 && (
                            <span className="text-[9px] text-sky-400 bg-sky-500/10 border border-sky-500/20 px-1.5 py-0.5 rounded">{b.guest_count} khách</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-900 dark:text-slate-500 dark:text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.date}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.start_time}-{b.end_time}</span>
                          {b.receipt_id && (
                            <span className="flex items-center gap-1 text-cyan-400 font-mono">Mã GD: {b.receipt_id}</span>
                          )}
                        </div>
                        {createdAtStr && <p className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mt-0.5">Đặt lúc: {createdAtStr}</p>}
                      </div>
                      <div className="text-right space-y-1 shrink-0 ml-3">
                        <span className="text-sm font-bold text-lime-400">{formatVND(b.total_price)}đ</span>
                        {b.guest_count > 0 && <p className="text-[9px] text-slate-900 dark:text-slate-500 dark:text-slate-500">{b.guest_count} khách</p>}
                        {/* Payment badges */}
                        {b.payment_verified_at ? (
                          <span className="flex items-center gap-1 text-[9px] text-emerald-400 justify-end"><BadgeCheck className="w-3 h-3" /> Đã XN TT</span>
                        ) : b.payment_confirmed_at ? (
                          <span className="flex items-center gap-1 text-[9px] text-amber-400 justify-end"><CircleDollarSign className="w-3 h-3" /> Chờ XN TT</span>
                        ) : null}
                      </div>
                    </div>
                    {/* Payment actions */}
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-300 dark:border-[#334155]/50">
                      {b.status === "confirmed" && !b.payment_confirmed_at && (
                        <button onClick={() => handleConfirmPayment(b.id)}
                          className="text-[10px] bg-sky-500 hover:bg-sky-600 text-white font-semibold px-2.5 py-1 rounded-md flex items-center gap-1"
                          data-testid={`hist-pay-${b.id}`}>
                          <Upload className="w-3 h-3" /> Tải ảnh xác nhận T.Toán
                        </button>
                      )}
                      {((b.receipt_images && b.receipt_images.length > 0) || b.receipt_image_url) && (
                        <button onClick={() => {
                          const url = b.receipt_images?.length > 0 ? b.receipt_images[b.receipt_images.length - 1].url : b.receipt_image_url;
                          if (url) setViewReceiptUrl(url);
                        }}
                          className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1 transition-colors">
                          <ImageIcon className="w-3 h-3" /> Xem biên nhận
                        </button>
                      )}
                      {b.payment_confirmed_at && !b.payment_verified_at && (
                        <button onClick={() => handleConfirmPayment(b.id)}
                          className="text-[10px] text-slate-900 dark:text-slate-500 dark:text-slate-500 hover:text-sky-400 flex items-center gap-1 transition-colors">
                          <Upload className="w-3 h-3" /> Tải lại biên nhận
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Book modal — compact with invoice */}
      <Dialog open={bookModalOpen} onOpenChange={setBookModalOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] sm:max-w-[380px] max-h-[90vh] overflow-y-auto p-4" data-testid="customer-book-modal">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-slate-900 dark:text-[#F8FAFC] text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-400" /> Đặt sân
            </DialogTitle>
            <DialogDescription className="text-slate-900 dark:text-slate-500 dark:text-slate-500 text-xs">{bookCourt?.name} — {formatDateVN(selectedDate)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Time selectors */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-0.5 block">Bắt đầu</label>
                <Select
                  value={bookStart}
                  onValueChange={(v) => {
                    setBookStart(v);
                    // Auto-cap end time = min(start+2h, end-of-day, max-allowed-by-conflict)
                    const startT = timeToMin(v);
                    const maxByConflict = bookCourt?.id ? getMaxAllowedEnd(bookCourt.id, startT, { excludeMain: true }) : endMin;
                    const eM = Math.min(startT + 120, endMin, maxByConflict);
                    setBookEnd(minToTime(Math.max(eM, startT + 30)));
                  }}
                >
                  <SelectTrigger className="bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-8 text-xs" data-testid="book-start-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                    {TIME_OPTIONS.filter(t => timeToMin(t) >= startMin && timeToMin(t) < endMin).map(t => {
                      const blocked = bookCourt?.id ? isStartBlocked(bookCourt.id, timeToMin(t), { excludeMain: true }) : false;
                      return (
                        <SelectItem
                          key={t}
                          value={t}
                          disabled={blocked}
                          className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                          data-testid={`book-start-opt-${t}${blocked ? "-blocked" : ""}`}
                        >
                          {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-0.5 block">Kết thúc</label>
                <Select value={bookEnd} onValueChange={setBookEnd}>
                  <SelectTrigger className="bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-8 text-xs" data-testid="book-end-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                    {(() => {
                      // End options theo START đã chọn + chặn cross-conflict
                      if (!bookStart) return null;
                      const sMin = timeToMin(bookStart);
                      const maxAllow = bookCourt?.id ? getMaxAllowedEnd(bookCourt.id, sMin, { excludeMain: true }) : endMin;
                      return TIME_OPTIONS
                        .filter(t => timeToMin(t) > sMin && timeToMin(t) <= endMin)
                        .map(t => {
                          const tMin = timeToMin(t);
                          const blocked = tMin > maxAllow;  // vượt qua booking khác → cấm
                          return (
                            <SelectItem
                              key={t}
                              value={t}
                              disabled={blocked}
                              className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                              data-testid={`book-end-opt-${t}${blocked ? "-blocked" : ""}`}
                            >
                              {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                            </SelectItem>
                          );
                        });
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Multi-time: khung giờ thêm cho CÙNG sân chính (chỉ regular court) */}
            {bookCourt?.court_type !== "social" && (
              <div className="border border-slate-300 dark:border-[#334155] rounded-xl p-2.5 bg-slate-100/50 dark:bg-[#0f172a]/40" data-testid="same-court-section">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] text-slate-600 dark:text-slate-400 uppercase font-medium">
                    Khung giờ thêm cho cùng sân ({bookCourt?.name}) {sameCourtSlots.length > 0 && (
                      <span className="text-teal-500 normal-case">({sameCourtSlots.length})</span>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={addSameCourtSlot}
                    className="text-[10px] text-teal-500 hover:text-teal-400 flex items-center gap-1"
                    data-testid="add-same-court-slot-btn"
                  >
                    <Plus className="w-3 h-3" /> Thêm khung giờ
                  </button>
                </div>
                {sameCourtSlots.length === 0 ? (
                  <p className="text-[10px] text-slate-500 dark:text-slate-600 italic">
                    Bấm "Thêm khung giờ" để đặt {bookCourt?.name} ở nhiều khung khác nhau (vd 10-12h và 14-16h).
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {sameCourtSlots.map((s, idx) => {
                      const sMin = timeToMin(s.start_time);
                      const slotMaxEnd = getMaxAllowedEnd(bookCourt.id, sMin, { excludeSameIdx: idx });
                      return (
                        <div
                          key={idx}
                          className="grid grid-cols-12 gap-1.5 items-center"
                          data-testid={`same-court-slot-row-${idx}`}
                        >
                          <span className="col-span-5 text-[11px] text-slate-700 dark:text-slate-300 pl-1 truncate">
                            <Clock className="w-3 h-3 inline mr-1 text-teal-500" />
                            {bookCourt?.name}
                          </span>
                          <Select
                            value={s.start_time}
                            onValueChange={(v) => {
                              const sM = timeToMin(v);
                              const newMax = getMaxAllowedEnd(bookCourt.id, sM, { excludeSameIdx: idx });
                              const eM = timeToMin(s.end_time);
                              let newEnd = s.end_time;
                              if (eM <= sM || eM > newMax) {
                                newEnd = minToTime(Math.max(Math.min(sM + 60, endMin, newMax), sM + 30));
                              }
                              updateSameCourtSlot(idx, { start_time: v, end_time: newEnd });
                            }}
                          >
                            <SelectTrigger className="col-span-3 bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-7 text-[11px]" data-testid={`same-court-slot-start-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                              {TIME_OPTIONS.filter(t => timeToMin(t) >= startMin && timeToMin(t) < endMin).map(t => {
                                const blocked = isStartBlocked(bookCourt.id, timeToMin(t), { excludeSameIdx: idx });
                                return (
                                  <SelectItem
                                    key={t}
                                    value={t}
                                    disabled={blocked}
                                    className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                                    data-testid={`same-court-slot-${idx}-start-opt-${t}${blocked ? "-blocked" : ""}`}
                                  >
                                    {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Select
                            value={s.end_time}
                            onValueChange={(v) => updateSameCourtSlot(idx, { end_time: v })}
                          >
                            <SelectTrigger className="col-span-3 bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-7 text-[11px]" data-testid={`same-court-slot-end-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                              {TIME_OPTIONS
                                .filter(t => timeToMin(t) > sMin && timeToMin(t) <= endMin)
                                .map(t => {
                                  const tMin = timeToMin(t);
                                  const blocked = tMin > slotMaxEnd;
                                  return (
                                    <SelectItem
                                      key={t}
                                      value={t}
                                      disabled={blocked}
                                      className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                                      data-testid={`same-court-slot-${idx}-end-opt-${t}${blocked ? "-blocked" : ""}`}
                                    >
                                      {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                                    </SelectItem>
                                  );
                                })}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => removeSameCourtSlot(idx)}
                            className="col-span-1 w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-red-400 hover:border-red-500/50 hover:bg-red-500/10"
                            data-testid={`same-court-slot-remove-${idx}`}
                            aria-label="Xoá khung giờ này"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Multi-court: thêm sân khác (chỉ regular court) */}
            {bookCourt?.court_type !== "social" && (
              <div className="border border-slate-300 dark:border-[#334155] rounded-xl p-2.5 bg-slate-100/50 dark:bg-[#0f172a]/40" data-testid="multi-court-section">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 uppercase font-medium">
                    Sân khác trong cùng lượt {extraSlots.length > 0 && (
                      <span className="text-teal-400 normal-case">({extraSlots.length})</span>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={addExtraSlot}
                    disabled={availableCourtsForExtra.length === 0}
                    className="text-[10px] text-teal-400 hover:text-teal-300 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="add-extra-slot-btn"
                  >
                    <Plus className="w-3 h-3" /> Thêm sân
                  </button>
                </div>

                {extraSlots.length === 0 ? (
                  <p className="text-[10px] text-slate-400 dark:text-slate-600 italic">Bấm "Thêm sân" để đặt nhiều sân cùng lượt (mỗi sân chọn khung giờ riêng).</p>
                ) : (
                  <div className="space-y-1.5">
                    {extraSlots.map((s, idx) => {
                      // Court khả dụng cho slot này = regular courts - các court đang dùng (loại trừ slot hiện tại để có thể reselect)
                      const usedExceptSelf = new Set();
                      if (bookCourt?.id) usedExceptSelf.add(bookCourt.id);
                      extraSlots.forEach((es, i) => {
                        if (i !== idx && es.court_id) usedExceptSelf.add(es.court_id);
                      });
                      const courtChoices = regularCourts.filter(c => !usedExceptSelf.has(c.id));
                      const slotStartMin = timeToMin(s.start_time);
                      const slotMaxEnd = s.court_id ? getMaxAllowedEnd(s.court_id, slotStartMin, { excludeExtraIdx: idx }) : endMin;
                      return (
                        <div
                          key={idx}
                          className="grid grid-cols-12 gap-1.5 items-center"
                          data-testid={`extra-slot-row-${idx}`}
                        >
                          <Select
                            value={s.court_id}
                            onValueChange={(v) => {
                              // Khi đổi sân: tự cap end_time nếu vượt giới hạn conflict mới
                              const newMax = getMaxAllowedEnd(v, slotStartMin, { excludeExtraIdx: idx });
                              const newEnd = timeToMin(s.end_time) > newMax
                                ? minToTime(Math.max(newMax, slotStartMin + 30))
                                : s.end_time;
                              updateExtraSlot(idx, { court_id: v, end_time: newEnd });
                            }}
                          >
                            <SelectTrigger className="col-span-5 bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-7 text-[11px]" data-testid={`extra-slot-court-${idx}`}>
                              <SelectValue placeholder="Chọn sân" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                              {courtChoices.map(c => (
                                <SelectItem key={c.id} value={c.id} className="text-slate-700 dark:text-slate-300 text-xs">{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={s.start_time}
                            onValueChange={(v) => {
                              const sMin = timeToMin(v);
                              const newMax = s.court_id ? getMaxAllowedEnd(s.court_id, sMin, { excludeExtraIdx: idx }) : endMin;
                              const eMin = timeToMin(s.end_time);
                              let newEnd = s.end_time;
                              if (eMin <= sMin || eMin > newMax) {
                                newEnd = minToTime(Math.max(Math.min(sMin + 120, endMin, newMax), sMin + 30));
                              }
                              updateExtraSlot(idx, { start_time: v, end_time: newEnd });
                            }}
                          >
                            <SelectTrigger className="col-span-3 bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-7 text-[11px]" data-testid={`extra-slot-start-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                              {TIME_OPTIONS.filter(t => timeToMin(t) >= startMin && timeToMin(t) < endMin).map(t => {
                                const blocked = s.court_id ? isStartBlocked(s.court_id, timeToMin(t), { excludeExtraIdx: idx }) : false;
                                return (
                                  <SelectItem
                                    key={t}
                                    value={t}
                                    disabled={blocked}
                                    className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                                    data-testid={`extra-slot-${idx}-start-opt-${t}${blocked ? "-blocked" : ""}`}
                                  >
                                    {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Select
                            value={s.end_time}
                            onValueChange={(v) => updateExtraSlot(idx, { end_time: v })}
                          >
                            <SelectTrigger className="col-span-3 bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-7 text-[11px]" data-testid={`extra-slot-end-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] max-h-48">
                              {TIME_OPTIONS
                                .filter(t => timeToMin(t) > slotStartMin && timeToMin(t) <= endMin)
                                .map(t => {
                                  const tMin = timeToMin(t);
                                  const blocked = tMin > slotMaxEnd;
                                  return (
                                    <SelectItem
                                      key={t}
                                      value={t}
                                      disabled={blocked}
                                      className={`text-xs ${blocked ? "text-slate-400 dark:text-slate-600 line-through cursor-not-allowed opacity-50" : "text-slate-700 dark:text-slate-300"}`}
                                      data-testid={`extra-slot-${idx}-end-opt-${t}${blocked ? "-blocked" : ""}`}
                                    >
                                      {t} {blocked && <span className="ml-1 text-[9px] text-red-400">(Đã đặt)</span>}
                                    </SelectItem>
                                  );
                                })}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => removeExtraSlot(idx)}
                            className="col-span-1 w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-red-400 hover:border-red-500/50 hover:bg-red-500/10"
                            data-testid={`extra-slot-remove-${idx}`}
                            aria-label="Xoá sân này"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Guest count — compact */}
            <div>
              <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-0.5 block">Số người chơi</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-teal-500 hover:text-teal-400"
                  data-testid="guest-minus">−</button>
                <Input type="number" min={1} max={50} value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-12 h-7 text-center bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid="guest-count-input" />
                <button onClick={() => setGuestCount(Math.min(50, guestCount + 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-teal-500 hover:text-teal-400"
                  data-testid="guest-plus">+</button>
              </div>
            </div>

            {/* DỤNG CỤ CẦN CHUẨN BỊ — addons (chỉ regular court) */}
            {bookCourt?.court_type !== "social" && (
              <div className="border border-slate-300 dark:border-[#334155] rounded-xl p-2.5 bg-slate-100/50 dark:bg-[#0f172a]/40" data-testid="addons-section">
                <p className="text-[10px] text-slate-600 dark:text-slate-400 uppercase font-medium mb-2">
                  Dụng cụ cần chuẩn bị (tuỳ chọn)
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {/* Nhặt bóng */}
                  <button
                    type="button"
                    onClick={() => setAddonBallPicker(v => !v)}
                    className={`flex items-center gap-1.5 rounded-lg border p-2 text-[11px] transition ${
                      addonBallPicker
                        ? "border-teal-500 bg-teal-500/10 text-teal-600 dark:text-teal-400"
                        : "border-slate-300 dark:border-[#334155] text-slate-700 dark:text-slate-300 hover:border-teal-500/50"
                    }`}
                    data-testid="addon-ball-picker-btn"
                    aria-pressed={addonBallPicker}
                  >
                    <Volleyball className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Nhặt bóng</span>
                    {addonBallPicker && <BadgeCheck className="w-3 h-3 ml-auto text-teal-500" />}
                  </button>
                  {/* Máy bắn bóng */}
                  <button
                    type="button"
                    onClick={() => setAddonBallMachine(v => !v)}
                    className={`flex items-center gap-1.5 rounded-lg border p-2 text-[11px] transition ${
                      addonBallMachine
                        ? "border-sky-500 bg-sky-500/10 text-sky-600 dark:text-sky-400"
                        : "border-slate-300 dark:border-[#334155] text-slate-700 dark:text-slate-300 hover:border-sky-500/50"
                    }`}
                    data-testid="addon-ball-machine-btn"
                    aria-pressed={addonBallMachine}
                  >
                    <Target className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Máy bắn bóng</span>
                    {addonBallMachine && <BadgeCheck className="w-3 h-3 ml-auto text-sky-500" />}
                  </button>
                  {/* Xe tập bóng */}
                  <button
                    type="button"
                    onClick={() => setAddonTrainingCart(v => !v)}
                    className={`flex items-center gap-1.5 rounded-lg border p-2 text-[11px] transition ${
                      addonTrainingCart
                        ? "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        : "border-slate-300 dark:border-[#334155] text-slate-700 dark:text-slate-300 hover:border-amber-500/50"
                    }`}
                    data-testid="addon-training-cart-btn"
                    aria-pressed={addonTrainingCart}
                  >
                    <Car className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Xe tập bóng</span>
                    {addonTrainingCart && <BadgeCheck className="w-3 h-3 ml-auto text-amber-500" />}
                  </button>
                  {/* Vợt — toggle + chọn số lượng */}
                  <div
                    className={`flex items-center gap-1.5 rounded-lg border p-2 text-[11px] transition ${
                      addonRacketCount > 0
                        ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400"
                        : "border-slate-300 dark:border-[#334155] text-slate-700 dark:text-slate-300"
                    }`}
                    data-testid="addon-racket-row"
                  >
                    <button
                      type="button"
                      onClick={() => setAddonRacketCount(c => (c > 0 ? 0 : 1))}
                      className="flex items-center gap-1 truncate"
                      data-testid="addon-racket-toggle"
                      aria-pressed={addonRacketCount > 0}
                    >
                      <span className="text-base shrink-0" aria-hidden>🎾</span>
                      <span className="truncate">Vợt</span>
                    </button>
                    {addonRacketCount > 0 && (
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setAddonRacketCount(c => Math.max(1, c - 1))}
                          className="w-5 h-5 rounded border border-fuchsia-500/40 flex items-center justify-center hover:bg-fuchsia-500/20"
                          data-testid="addon-racket-minus"
                        >−</button>
                        <span className="text-[11px] font-semibold w-3 text-center" data-testid="addon-racket-count">{addonRacketCount}</span>
                        <button
                          type="button"
                          onClick={() => setAddonRacketCount(c => Math.min(4, c + 1))}
                          className="w-5 h-5 rounded border border-fuchsia-500/40 flex items-center justify-center hover:bg-fuchsia-500/20"
                          data-testid="addon-racket-plus"
                        >+</button>
                      </div>
                    )}
                  </div>
                </div>
                {(addonBallPicker || addonBallMachine || addonTrainingCart || addonRacketCount > 0) && (sameCourtSlots.length > 0 || extraSlots.length > 0) && (
                  <p className="text-[9px] text-slate-500 dark:text-slate-500 mt-1.5 italic">
                    Lưu ý: phụ kiện chỉ áp dụng cho khung đầu (sân chính) khi đặt nhiều khung/sân, tránh tính phí trùng.
                  </p>
                )}
              </div>
            )}

            {/* Notes — smaller */}
            <div>
              <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-0.5 block">Ghi chú</label>
              <Input value={bookNotes} onChange={(e) => setBookNotes(e.target.value)} placeholder="Ghi chú (tuỳ chọn)"
                className="bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-8 text-xs" />
            </div>

            {/* Invoice summary */}
            <div className="bg-slate-100 dark:bg-[#0f172a] border border-teal-500/20 rounded-xl p-3" data-testid="booking-invoice">
              <p className="text-[9px] text-slate-900 dark:text-slate-500 dark:text-slate-500 uppercase mb-2 font-medium">
                Tổng hợp {(extraSlots.length + sameCourtSlots.length) > 0 && (
                  <span className="text-teal-500 normal-case">
                    ({1 + sameCourtSlots.length + extraSlots.length} khung)
                  </span>
                )}
              </p>
              <div className="space-y-1.5">
                {(extraSlots.length + sameCourtSlots.length) > 0 ? (
                  // Multi-slot: list từng khung + ngày + giờ
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600 dark:text-slate-400">Ngày</span>
                      <span className="text-slate-800 dark:text-slate-200">{formatDateVN(selectedDate)}</span>
                    </div>
                    <div className="border-t border-slate-300 dark:border-[#334155] pt-1.5 space-y-1">
                      {[
                        { court_name: bookCourt?.name, start_time: bookStart, end_time: bookEnd },
                        ...sameCourtSlots.map(s => ({
                          court_name: bookCourt?.name,
                          start_time: s.start_time, end_time: s.end_time,
                        })),
                        ...extraSlots.map(s => ({
                          court_name: regularCourts.find(c => c.id === s.court_id)?.name || "—",
                          start_time: s.start_time, end_time: s.end_time,
                        })),
                      ].map((slot, i) => (
                        <div key={i} className="flex justify-between text-[11px]" data-testid={`summary-slot-${i}`}>
                          <span className="text-slate-700 dark:text-slate-300">{slot.court_name}</span>
                          <span className="text-slate-600 dark:text-slate-400">
                            {slot.start_time}-{slot.end_time}
                            <span className="text-slate-500 dark:text-slate-600 ml-1">
                              ({(timeToMin(slot.end_time) - timeToMin(slot.start_time)) / 60}h)
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Số người</span>
                      <span className="text-slate-800 dark:text-slate-200">{guestCount} người</span>
                    </div>
                    <div className="border-t border-slate-300 dark:border-[#334155] mt-2 pt-2 flex justify-between">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Trạng thái</span>
                      <span className="text-xs font-semibold text-amber-400">Chờ xác nhận</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Sân</span>
                      <span className="text-slate-800 dark:text-slate-200 font-medium">{bookCourt?.name}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Ngày</span>
                      <span className="text-slate-800 dark:text-slate-200">{formatDateVN(selectedDate)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Thời gian</span>
                      <span className="text-slate-800 dark:text-slate-200">{bookStart} - {bookEnd} ({bookStart && bookEnd ? `${(timeToMin(bookEnd) - timeToMin(bookStart)) / 60}h` : "—"})</span>
                    </div>
                    {bookCourt?.court_type === "social" ? (
                      <>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Loại vé</span>
                          <span className="text-teal-400">{bookStart && timeToMin(bookStart) >= 945 ? "Vé chiều" : "Vé sáng"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Đơn giá</span>
                          <span className="text-slate-800 dark:text-slate-200">{bookStart && timeToMin(bookStart) >= 945 ? "80.000đ" : "50.000đ"}/vé</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Số vé</span>
                          <span className="text-slate-800 dark:text-slate-200">{guestCount} vé</span>
                        </div>
                        <div className="border-t border-slate-300 dark:border-[#334155] mt-2 pt-2 flex justify-between">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Tổng cộng</span>
                          <span className="text-sm font-bold text-teal-400">
                            {formatVND((bookStart && timeToMin(bookStart) >= 945 ? 80000 : 50000) * guestCount)}đ
                          </span>
                        </div>
                        <p className="text-[9px] text-slate-400 dark:text-slate-600 text-right">
                          {guestCount} người x {bookStart && timeToMin(bookStart) >= 945 ? "80.000đ" : "50.000đ"} = {formatVND((bookStart && timeToMin(bookStart) >= 945 ? 80000 : 50000) * guestCount)}đ
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Số người</span>
                          <span className="text-slate-800 dark:text-slate-200">{guestCount} người</span>
                        </div>
                        <div className="border-t border-slate-300 dark:border-[#334155] mt-2 pt-2 flex justify-between">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Trạng thái</span>
                          <span className="text-xs font-semibold text-amber-400">Chờ xác nhận</span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="ghost" onClick={() => setBookModalOpen(false)} className="text-slate-400 dark:text-slate-600 dark:text-slate-400 h-8 text-xs">Huỷ</Button>
            <Button onClick={handleSubmitBooking} disabled={submitting || !bookStart || !bookEnd}
              className="bg-teal-500 hover:bg-teal-600 text-[#0f172a] font-bold gap-1.5 h-8 text-xs" data-testid="confirm-book-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
              {(() => {
                const total = 1 + sameCourtSlots.length + extraSlots.length;
                if (submitting) return "Đang đặt...";
                if (total === 1) return "Xác nhận đặt sân";
                return `Đặt ${total} khung`;
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Booking Modal */}
      <Dialog open={socialModalOpen} onOpenChange={setSocialModalOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] border-slate-300 dark:border-[#334155] sm:max-w-[360px] p-4" data-testid="social-book-modal">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-teal-400 text-base flex items-center gap-2">
              <Zap className="w-4 h-4" /> Đặt vé Social
            </DialogTitle>
            <DialogDescription className="text-slate-900 dark:text-slate-500 dark:text-slate-500 text-xs">{formatDateVN(selectedDate)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Session toggle */}
            <div>
              <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-1.5 block">Chọn buổi</label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setSocialSession("morning")}
                  className={`p-3 rounded-xl border text-center transition-all ${socialSession === "morning" ? "bg-teal-500/15 border-teal-500/40 text-teal-400" : "bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-slate-500"}`}
                  data-testid="social-morning-btn">
                  <p className="text-sm font-bold">Sáng</p>
                  <p className="text-[10px] mt-0.5">05:00 - 14:00</p>
                  <p className="text-xs font-bold mt-1 text-teal-400">50.000đ<span className="text-[9px] text-slate-900 dark:text-slate-500 dark:text-slate-500 font-normal">/vé</span></p>
                </button>
                <button onClick={() => setSocialSession("evening")}
                  className={`p-3 rounded-xl border text-center transition-all ${socialSession === "evening" ? "bg-amber-500/15 border-amber-500/40 text-amber-400" : "bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-slate-500"}`}
                  data-testid="social-evening-btn">
                  <p className="text-sm font-bold">Chiều tối</p>
                  <p className="text-[10px] mt-0.5">14:00 - 22:00</p>
                  <p className="text-xs font-bold mt-1 text-amber-400">80.000đ<span className="text-[9px] text-slate-900 dark:text-slate-500 dark:text-slate-500 font-normal">/vé</span></p>
                </button>
              </div>
            </div>

            {/* Guest count */}
            <div>
              <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-1 block">Số vé (người)</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setSocialGuests(Math.max(1, socialGuests - 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-teal-500 hover:text-teal-400">−</button>
                <Input type="number" min={1} max={50} value={socialGuests}
                  onChange={(e) => setSocialGuests(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-14 h-7 text-center bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                <button onClick={() => setSocialGuests(Math.min(50, socialGuests + 1))}
                  className="w-7 h-7 rounded-lg border border-slate-300 dark:border-[#334155] flex items-center justify-center text-slate-400 dark:text-slate-600 dark:text-slate-400 hover:border-teal-500 hover:text-teal-400">+</button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-[10px] text-slate-400 dark:text-slate-600 dark:text-slate-400 mb-0.5 block">Ghi chú</label>
              <Input value={socialNotes} onChange={(e) => setSocialNotes(e.target.value)} placeholder="Ghi chú (tuỳ chọn)"
                className="bg-slate-100 dark:bg-[#0f172a] border-slate-300 dark:border-[#334155] text-slate-800 dark:text-slate-200 h-8 text-xs" />
            </div>

            {/* Invoice */}
            <div className="bg-slate-100 dark:bg-[#0f172a] border border-teal-500/20 rounded-xl p-3" data-testid="social-invoice">
              <p className="text-[9px] text-slate-900 dark:text-slate-500 dark:text-slate-500 uppercase mb-2 font-medium">Tổng hợp</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Buổi</span>
                  <span className={socialSession === "morning" ? "text-teal-400 font-medium" : "text-amber-400 font-medium"}>
                    {socialSession === "morning" ? "Sáng (05:00 - 14:00)" : "Chiều tối (14:00 - 22:00)"}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Đơn giá</span>
                  <span className="text-slate-800 dark:text-slate-200">{socialSession === "morning" ? "50.000đ" : "80.000đ"}/vé</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400 dark:text-slate-600 dark:text-slate-400">Số vé</span>
                  <span className="text-slate-800 dark:text-slate-200">{socialGuests} vé</span>
                </div>
                <div className="border-t border-slate-300 dark:border-[#334155] mt-2 pt-2 flex justify-between">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Tổng cộng</span>
                  <span className="text-sm font-bold text-teal-400">
                    {formatVND((socialSession === "morning" ? 50000 : 80000) * socialGuests)}đ
                  </span>
                </div>
                <p className="text-[9px] text-slate-400 dark:text-slate-600 text-right">
                  {socialGuests} người x {socialSession === "morning" ? "50.000đ" : "80.000đ"} = {formatVND((socialSession === "morning" ? 50000 : 80000) * socialGuests)}đ
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="ghost" onClick={() => setSocialModalOpen(false)} className="text-slate-400 dark:text-slate-600 dark:text-slate-400 h-8 text-xs">Huỷ</Button>
            <Button onClick={handleSubmitSocial} disabled={socialSubmitting}
              className="bg-teal-500 hover:bg-teal-400 text-[#0f172a] font-bold gap-1.5 h-8 text-xs" data-testid="confirm-social-btn">
              {socialSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {socialSubmitting ? "Đang đặt..." : "Xác nhận đặt Social"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhoneVerifyDialog open={phoneVerifyOpen} onOpenChange={setPhoneVerifyOpen} onVerified={() => window.location.reload()} />
      <PaymentReceiptDialog
        open={receiptDialogOpen}
        onOpenChange={setReceiptDialogOpen}
        bookingId={receiptBookingId}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["my-bookings"] })}
      />

      {/* Receipt image viewer */}
      {viewReceiptUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
          onClick={() => setViewReceiptUrl(null)} data-testid="receipt-viewer-overlay">
          <div className="relative max-w-lg max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img src={viewReceiptUrl} alt="Biên nhận" className="max-w-full max-h-[80vh] rounded-lg object-contain border border-slate-300 dark:border-[#334155]" />
            <button onClick={() => setViewReceiptUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
